import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import PhotoUploader from "../components/PhotoUploader";
import BoostBox from "../components/BoostBox";
import { isPremium, planLimit } from "../hooks/usePlanSettings";
import PricePicker from "../components/PricePicker";
import categories from "../data/categories";
import cities from "../data/cities";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { isLive } from "../hooks/useVendors";

export default function Manage() {
  const { t } = useLang();
  const { user, loading, login, logout } = useAuth();
  if (loading)
    return (
      <>
        <Navbar />
        <Center>…</Center>
      </>
    );
  if (!user) return <VendorLogin t={t} login={login} />;
  return <Manager t={t} user={user} logout={logout} />;
}

/* ---------- Login ---------- */
function VendorLogin({ t, login }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch {
      setError(t("manage.loginError"));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setResetSent(false);
    if (!email.trim()) {
      setError(t("auth.enterEmail"));
      return;
    }
    try {
      await resetPassword(email.trim());
      setError("");
      setResetSent(true);
    } catch {
      setError(t("auth.resetError"));
    }
  };

  return (
    <>
      <Navbar />
      <Center>
        <Card>
          <h1>{t("manage.loginTitle")}</h1>
          <p>{t("manage.loginSub")}</p>
          <input type="email" placeholder={t("list.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
          <input type="password" placeholder={t("list.password")} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          {error && <span className="err">{error}</span>}
          {resetSent && <span className="ok">{t("auth.resetSent")}</span>}
          <button onClick={submit} disabled={busy}>{t("manage.login")}</button>
          <button type="button" className="forgot" onClick={forgot}>{t("auth.forgot")}</button>
          <div className="foot">
            {t("manage.noAccount")} <Link to="/list">{t("manage.register")}</Link>
          </div>
        </Card>
      </Center>
    </>
  );
}

/* ---------- Manager ---------- */
function Manager({ t, user, logout }) {
  const { lang } = useLang();
  const [vendors, setVendors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingList(true);
      const q = query(collection(db, "vendors"), where("ownerUid", "==", user.uid));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setVendors(list);
      if (list.length) setSelectedId(list[0].id);
      setLoadingList(false);
    })();
  }, [user.uid]);

  useEffect(() => {
    const v = vendors.find((x) => x.id === selectedId);
    if (v) {
      setForm({
        businessName: v.businessName || "",
        category: v.category || "",
        accountType: v.accountType || "company",
        city: v.city || "",
        priceRange: v.priceRange || "€€",
        priceMode: v.priceMode || (v.priceRange ? "tier" : "none"),
        priceMin: v.priceMin || "",
        priceMax: v.priceMax || "",
        priceFrom: v.priceFrom || "",
        phone: v.phone || "",
        whatsapp: v.whatsapp || "",
        instagram: v.instagram || "",
        description: v.description || "",
        videoUrl: v.videoUrl || "",
        website: v.website || "",
        extraCategories: Array.isArray(v.extraCategories) ? v.extraCategories : [],
      });
      setPhotos(v.photos || []);
    }
  }, [selectedId, vendors]);

  const current = vendors.find((x) => x.id === selectedId);
  const allowIndividual = (() => {
    const c = categories.find((x) => x.id === form?.category);
    return c ? c.allowIndividual : false;
  })();

  const set = (key, value) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (key === "category") {
        const cat = categories.find((c) => c.id === value);
        if (cat && !cat.allowIndividual) next.accountType = "company";
      }
      return next;
    });

  const save = async () => {
    if (!selectedId || !form) return;
    setSaving(true);
    setSaved(false);
    try {
      const clean = {
        ...form,
        priceRange: form.priceMode === "tier" ? form.priceRange : "",
        priceMin: form.priceMode === "range" ? form.priceMin : "",
        priceMax: form.priceMode === "range" ? form.priceMax : "",
        priceFrom: form.priceMode === "from" ? form.priceFrom : "",
      };
      await updateDoc(doc(db, "vendors", selectedId), { ...clean, photos });
      setVendors((vs) => vs.map((v) => (v.id === selectedId ? { ...v, ...clean, photos } : v)));
      setSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Save failed:", err.message || err);
    } finally {
      setSaving(false);
    }
  };

  const statusKey = (v) =>
    !v.isActive ? (v.status === "expired" ? "Expired" : "Pending") : isLive(v) ? "Active" : "Expired";

  if (loadingList) {
    return (
      <>
        <Navbar />
        <Center>…</Center>
      </>
    );
  }

  if (!vendors.length) {
    return (
      <>
        <Navbar />
        <Center>
          <p>{t("manage.none")}</p>
          <Link to="/list">{t("manage.register")}</Link>
          <button className="link" onClick={logout}>{t("manage.logout")}</button>
        </Center>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Wrap>
        <TopBar>
          <h1>{t("manage.title")}</h1>
          <button onClick={logout}>{t("manage.logout")}</button>
        </TopBar>

        {vendors.length > 1 && (
          <Selector>
            {vendors.map((v) => (
              <button key={v.id} className={v.id === selectedId ? "on" : ""} onClick={() => setSelectedId(v.id)}>
                {v.businessName}
              </button>
            ))}
          </Selector>
        )}

        {current && (
          <StatusLine>
            {t("manage.statusLabel")}: <b>{t(`admin.status${statusKey(current)}`)}</b>
            {current.isActive && isLive(current) && current.paidUntil && (
              <span>
                {" · "}
                {Math.max(0, Math.ceil((current.paidUntil.seconds * 1000 - Date.now()) / 86400000))}{" "}
                {t("admin.daysLeft")}
              </span>
            )}
          </StatusLine>
        )}

        {current && <BoostBox vendor={current} user={user} />}

        {saved && <Saved>{t("manage.saved")}</Saved>}

        {form && (
          <Form>
            <Field>
              <label>{t("list.businessName")}</label>
              <input value={form.businessName} onChange={(e) => set("businessName", e.target.value)} />
            </Field>
            <Row>
              <Field>
                <label>{t("list.category")}</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)}>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c[lang]}</option>
                  ))}
                </select>
              </Field>
              <Field>
                <label>{t("list.city")}</label>
                <select value={form.city} onChange={(e) => set("city", e.target.value)}>
                  {cities.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
            </Row>
            {allowIndividual && (
              <Field>
                <label>{t("list.accountType")}</label>
                <Segmented>
                  <button type="button" className={form.accountType === "company" ? "on" : ""} onClick={() => set("accountType", "company")}>{t("list.company")}</button>
                  <button type="button" className={form.accountType === "individual" ? "on" : ""} onClick={() => set("accountType", "individual")}>{t("list.individual")}</button>
                </Segmented>
              </Field>
            )}
            <Field>
              <label>{t("price.label")}</label>
              <PricePicker value={form} onChange={set} />
            </Field>
            <Row>
              <Field>
                <label>{t("list.phone")}</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </Field>
            </Row>
            <Row>
              <Field>
                <label>{t("list.whatsapp")}</label>
                <input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} />
              </Field>
              <Field>
                <label>{t("list.instagram")}</label>
                <input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} />
              </Field>
            </Row>
            <Field>
              <label>{t("list.description")}</label>
              <textarea rows={4} maxLength={1000} value={form.description} onChange={(e) => set("description", e.target.value)} />
            </Field>
            <Field>
              <label>{t("list.photos")} <small>{t("list.photosHint")}</small></label>
              <PhotoUploader photos={photos} setPhotos={setPhotos} max={planLimit(current.tier).photos} />
            </Field>

            {isPremium(current) ? (
              <PremiumFields>
                <h3>★ {t("manage.premiumExtras")}</h3>
                <Field>
                  <label>{t("plan.video")} <small>{t("plan.videoHint")}</small></label>
                  <input value={form.videoUrl} onChange={(e) => set("videoUrl", e.target.value)} placeholder="https://youtube.com/..." />
                </Field>
                <Field>
                  <label>{t("plan.website")}</label>
                  <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
                </Field>
                <Field>
                  <label>{t("plan.extraCats")} <small>{t("plan.extraCatsHint")}</small></label>
                  <CatGrid>
                    {categories
                      .filter((c) => c.id !== form.category)
                      .map((c) => {
                        const sel = form.extraCategories.includes(c.id);
                        const full = form.extraCategories.length >= 2 && !sel;
                        return (
                          <CatChip
                            key={c.id}
                            type="button"
                            $on={sel}
                            disabled={full}
                            onClick={() =>
                              set(
                                "extraCategories",
                                sel
                                  ? form.extraCategories.filter((x) => x !== c.id)
                                  : [...form.extraCategories, c.id]
                              )
                            }
                          >
                            {c[lang]}
                          </CatChip>
                        );
                      })}
                  </CatGrid>
                </Field>
              </PremiumFields>
            ) : (
              <Upsell>{t("manage.upsell")}</Upsell>
            )}

            <Primary onClick={save} disabled={saving}>
              {saving ? t("manage.saving") : t("manage.save")}
            </Primary>
          </Form>
        )}
      </Wrap>
    </>
  );
}

/* ---------- styles ---------- */
const Center = styled.div`
  min-height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  color: ${({ theme }) => theme.colors.inkSoft};
  a { color: ${({ theme }) => theme.colors.green}; font-weight: 500; }
  .link { background: none; border: none; color: ${({ theme }) => theme.colors.inkSoft}; cursor: pointer; font-size: 13px; text-decoration: underline; }
`;

const Card = styled.div`
  width: 100%;
  max-width: 380px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: ${({ theme }) => theme.radius};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 26px; color: ${({ theme }) => theme.colors.green}; }
  p { font-size: 14px; color: ${({ theme }) => theme.colors.inkSoft}; margin-top: -4px; margin-bottom: 4px; }
  input { font-family: inherit; font-size: 15px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 12px; padding: 12px 14px; outline: none; background: ${({ theme }) => theme.colors.cream}; }
  input:focus { border-color: ${({ theme }) => theme.colors.green}; }
  .err { color: ${({ theme }) => theme.colors.rose}; font-size: 13px; }
  .ok { color: ${({ theme }) => theme.colors.green}; font-size: 13px; }
  .forgot { background: transparent; border: none; color: ${({ theme }) => theme.colors.inkSoft}; font-size: 13px; cursor: pointer; text-decoration: underline; padding: 0; }
  .forgot:hover { color: ${({ theme }) => theme.colors.green}; }
  button { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border: none; border-radius: 100px; padding: 13px; font-size: 15px; font-weight: 500; cursor: pointer; }
  .foot { font-size: 13px; color: ${({ theme }) => theme.colors.inkSoft}; text-align: center; a { color: ${({ theme }) => theme.colors.green}; font-weight: 500; } }
`;

const Wrap = styled.div`
  max-width: 680px;
  margin: 0 auto;
  padding: 28px clamp(20px, 5vw, 40px) 80px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: clamp(26px, 4vw, 38px); color: ${({ theme }) => theme.colors.ink}; }
  button { background: transparent; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 100px; padding: 9px 18px; font-size: 14px; cursor: pointer; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Selector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  button { background: transparent; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 100px; padding: 8px 16px; font-size: 13.5px; cursor: pointer; color: ${({ theme }) => theme.colors.inkSoft}; }
  button.on { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border-color: ${({ theme }) => theme.colors.green}; }
`;

const StatusLine = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkSoft};
  margin-bottom: 16px;
  b { color: ${({ theme }) => theme.colors.ink}; }
`;

const Saved = styled.div`
  background: rgba(47, 74, 60, 0.1);
  color: ${({ theme }) => theme.colors.green};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 16px;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  label { font-size: 13.5px; font-weight: 500; color: ${({ theme }) => theme.colors.ink}; small { font-weight: 400; color: ${({ theme }) => theme.colors.inkSoft}; } }
  input, select, textarea {
    font-family: inherit; font-size: 15px; color: ${({ theme }) => theme.colors.ink};
    background: ${({ theme }) => theme.colors.surface}; border: 1px solid ${({ theme }) => theme.colors.hair};
    border-radius: 12px; padding: 12px 14px; outline: none; transition: border-color 0.18s;
  }
  input:focus, select:focus, textarea:focus { border-color: ${({ theme }) => theme.colors.green}; }
  textarea { resize: vertical; }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 560px) { flex-direction: column; }
`;

const Segmented = styled.div`
  display: inline-flex;
  background: ${({ theme }) => theme.colors.creamDeep};
  border-radius: 12px;
  padding: 4px;
  width: fit-content;
  button { border: none; background: transparent; cursor: pointer; font-size: 14px; font-weight: 500; color: ${({ theme }) => theme.colors.inkSoft}; padding: 9px 20px; border-radius: 9px; transition: 0.18s; }
  button.on { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
`;

const PremiumFields = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.gold};
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 18px; }
`;

const CatGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CatChip = styled.button`
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 100px;
  cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.colors.green : theme.colors.hair)};
  background: ${({ $on, theme }) => ($on ? theme.colors.green : "transparent")};
  color: ${({ $on, theme }) => ($on ? theme.colors.cream : theme.colors.inkSoft)};
  &:disabled { opacity: 0.4; cursor: default; }
`;

const Upsell = styled.div`
  background: rgba(201, 182, 142, 0.16);
  border: 1px dashed ${({ theme }) => theme.colors.gold};
  border-radius: 14px;
  padding: 16px 18px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.ink};
`;

const Primary = styled.button`
  align-self: flex-start;
  background: ${({ theme }) => theme.colors.green};
  color: ${({ theme }) => theme.colors.cream};
  border: none;
  border-radius: 100px;
  font-size: 15px;
  font-weight: 500;
  padding: 14px 34px;
  cursor: pointer;
  transition: 0.25s;
  &:hover { background: ${({ theme }) => theme.colors.greenDeep}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
